import { GridBaseDataProvider } from '../../../../grid/GridBaseDataProvider';
import {
  Column,
  ColumnKeys,
  SetFiltersRequest,
  GetDataGridItemsResponse,
  AssetType,
  DataGridItem,
  GridType,
  AssetListsResponse,
} from '../types';
import {
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
} from '../../../../grid/types/common';
import { sanitizeObject } from '../../../../utils/sanitization.utils';
import { filterRows } from '../../../../grid/utils/filters';
import { v4 as uuidv4 } from 'uuid';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';

export class EasmScanConfigurationAssetGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  private scanListData: Array<DataGridItem> = [];
  private scanExclusionData: Array<DataGridItem> = [];
  private filteredData: Array<DataGridItem> = [];
  private isDataLoaded = false;
  private gridType: GridType = GridType.SCAN_LIST;

  constructor(public dependencies: any, private companyId: string) {
    super();
  }

  private async populateGridData(): Promise<void> {
    const response = await ConsoleRequest.createRequest<AssetListsResponse>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'EASMScanConfigurations',
      method: 'getScheduleAssetListsByCompanyId',
      payload: {
        companyId: this.companyId,
      },
    });

    if (response.include) {
      this.scanListData = response.include;
      response.include?.forEach((item: DataGridItem) => {
        item.actions = 'actions';
        item.id = uuidv4();
      });
    }

    if (response.exclude) {
      this.scanExclusionData = response.exclude;
      response.exclude.forEach((item: DataGridItem) => {
        item.actions = 'actions';
        item.id = uuidv4();
      });
    }
  }

  public getScanListData(): Array<DataGridItem> {
    return this.scanListData;
  }

  public getScanExclusionData(): Array<DataGridItem> {
    return this.scanExclusionData;
  }

  public deleteItem(assetId: string, gridType: GridType): void {
    if (gridType === GridType.SCAN_LIST) {
      this.scanListData = this.scanListData.filter(item => item.id !== assetId);
    } else if (gridType === GridType.SCAN_EXCLUSIONS) {
      this.scanExclusionData = this.scanExclusionData.filter(item => item.id !== assetId);
    }
  }

  public async addAssetToScan(assetName: string, assetType: AssetType, gridType: GridType): Promise<void> {
    const nowDate = new Date().toISOString();

    const item: DataGridItem = {
      value: assetName,
      type: assetType,
      dateAdded: nowDate,
      isNowAdded: true,
      id: '',
      actions: '',
    };

    item.id = uuidv4();
    item.actions = 'actions';

    if (gridType === GridType.SCAN_LIST) {
      this.scanListData.unshift(item);
    } else if (gridType === GridType.SCAN_EXCLUSIONS) {
      this.scanExclusionData.unshift(item);
    }
  }

  public setGridType(gridType: GridType): void {
    this.gridType = gridType;
  }

  public getData(): Array<DataGridItem> {
    if (this.gridType === GridType.SCAN_LIST) {
      return this.scanListData;
    } else if (this.gridType === GridType.SCAN_EXCLUSIONS) {
      return this.scanExclusionData;
    }

    return [];
  }

  public getDataFromOtherTab(): Array<DataGridItem> {
    if (this.gridType === GridType.SCAN_LIST) {
      return this.scanExclusionData;
    } else if (this.gridType === GridType.SCAN_EXCLUSIONS) {
      return this.scanListData;
    }

    return [];
  }

  public setData(data: Array<DataGridItem>): void {
    if (this.gridType === GridType.SCAN_LIST) {
      this.scanListData = data;
    } else if (this.gridType === GridType.SCAN_EXCLUSIONS) {
      this.scanExclusionData = data;
    }
  }

  public async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    if (!this.isDataLoaded) {
      await this.populateGridData();

      this.filteredData = this.getData();
      this.isDataLoaded = true;
    }

    this.filteredData = filterRows(request.filters, this.getData());
    const summary = this.filteredData.length;

    return sanitizeObject({
      summary: summary + ' ' + this.dependencies.translateFn('items'),
      numItems: summary,
    });
  }

  public async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetDataGridItemsResponse> {
    if (!this.isDataLoaded) {
      await this.populateGridData();

      this.filteredData = this.getData();
      this.isDataLoaded = true;
    }

    this.filteredData = filterRows(request.filters, this.getData());

    if (request.pageNumber && request.pageSize) {
      const start = (request.pageNumber - 1) * request.pageSize;
      const end = start + request.pageSize;

      return sanitizeObject({ rawEvents: this.filteredData.slice(start, end) });
    }

    return sanitizeObject({ rawEvents: this.filteredData });
  }

  public async setFilters(request: SetFiltersRequest): Promise<{} | undefined> {
    return;
  }

  public async updateSettings(request: UpdateSettingsRequest<Column>): Promise<UpdateSettingsResponse> {
    return {};
  }
}
